import * as styles from "./SectionStandardSignPostCards.module.scss";

import { Button } from "../../../components/common";

import React from "react";

import StandardSignPostCard from "../../../components/SignPostCards/StandardSignPostCard";
import SuccessStoryCard from "../../../components/SignPostCards/SuccessStoryCard/SuccessStoryCard";
import { Colours } from "../../../utils/colours";
import { mapPromotedItemToSignpostCard } from "../../../utils/mappers";
import WrappedSection, {
	UnwrappedWrappedSection,
} from "../../WrappedSection/WrappedSection";

export interface SectionStandardSignPostCardsProps {
	title?: string;
	bodyHtmlText?: string;
	items: DogsTrust.SignPostCardProps[];
	link?: { title: string; uri: string };
	isSuccessStories?: boolean;
	withPageBreak?: boolean;
	withoutMargins?: boolean;
	forceFlatCards?: boolean;
	isSubComponent?: boolean;
	themeOverride?: string;
	variant?: string;
	isThreeColLayout?: boolean;
	jumpLinksTitle?: string;
}

export const getSectionStandardSignPostCardsProps = (
	section: Queries.SectionDataFragment,
): SectionStandardSignPostCardsProps => ({
	title: section.field_front_end_title,
	bodyHtmlText: section.field_section_short_description?.value,
	link: { ...section.field_link },
	items: section.promotedLinks.map(mapPromotedItemToSignpostCard),
});
export const SectionStandardSignPostCards: React.FC<
	SectionStandardSignPostCardsProps
> = ({
	title,
	bodyHtmlText,
	items,
	link,
	themeOverride,

	isSuccessStories = false,
	forceFlatCards = false,
	isSubComponent = false,
	variant = "",
	jumpLinksTitle,
}) => {
	if (!items?.length) {
		// Adding noise to builds, turn on if needed
		// console.warn(
		//     `Hiding ${
		//         isSuccessStories ? "SuccessStories" : "SignPostCards"
		//     }: (${title}) - No items to display.`
		// );
		return null;
	}
	items.sort((a, b) =>
		a.priority < b.priority ? -1 : a.priority < b.priority ? 1 : 0,
	);

	const checkIsFlat = (total: number, index: number) => {
		if (forceFlatCards) return true;
		if (total % 2 === 0) return false; // There is an even number of cards so all are square
		if (index % 2 === 0) return false; // There is two or more and this is an even number so cannot be orphan card

		if (index === 1 && total === 1) return true; // There is one card so it should be flat.
		if (index === total) return true; // There is an odd number of cards and this is the last one so it should be flat.

		return false;
	};

	const CardComponent = isSuccessStories
		? SuccessStoryCard
		: StandardSignPostCard;

	const getBiggestCardTextCount = () => {
		const biggestCard = items
			.filter((_item, i) => !checkIsFlat(items.length, i + 1))
			.map((item) => {
				return (item.title?.length || 0) + (item.body?.length || 0);
			});
		return biggestCard.reduce((a, b) => Math.max(a, b), 0);
	};

	const Wrapper = isSubComponent ? UnwrappedWrappedSection : WrappedSection;
	return (
		<Wrapper
			sectionName={`SectionStandardSignPostCards${variant}`}
			sectionColour={themeOverride ? themeOverride : Colours.WHITE}
		>
			<section className={styles.signpostcards}>
				{title && (
					<header className={styles.header}>
						{jumpLinksTitle ? (
							<h2 data-jumplinkstitle={jumpLinksTitle}>{title}</h2>
						) : (
							<h2>{title}</h2>
						)}
					</header>
				)}

				{bodyHtmlText && (
					<div
						className={styles.bodytext}
						// biome-ignore lint/security/noDangerouslySetInnerHtml: Safe source
						dangerouslySetInnerHTML={{
							__html: bodyHtmlText,
						}}
					/>
				)}
				<div className={styles.cardsection}>
					{items.map((item) => (
						<CardComponent
							key={item.title}
							{...item}
							biggestSiblingTextCount={getBiggestCardTextCount()}
						/>
					))}
				</div>
				{link?.uri && link.title && (
					<footer className={styles.buttonwrapper}>
						<Button url={link?.uri} variant={"tertiary"} fullWidth={true}>
							{link?.title}
						</Button>
					</footer>
				)}
			</section>
		</Wrapper>
	);
};

export default SectionStandardSignPostCards;
